import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaLock, FaGem, FaTicketAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';
import confetti from 'canvas-confetti';
import { API_URL } from '../config';
import WebApp from '@twa-dev/sdk';
import haptic from '../utils/haptic';

const STREAK_REWARDS = [
  { day: 1, gems: 10, tickets: 0 },
  { day: 2, gems: 20, tickets: 0 },
  { day: 3, gems: 30, tickets: 0 },
  { day: 4, gems: 40, tickets: 0 },
  { day: 5, gems: 50, tickets: 0 },
  { day: 6, gems: 60, tickets: 0 },
  { day: 7, gems: 100, tickets: 1 },
];

const DailyCheckIn = ({ userData, setUserData, APIKey }) => {
  const [loading, setLoading] = useState(false);
  const [canClaimToday, setCanClaimToday] = useState(false);
  const [currentStreak, setCurrentStreak] = useState(0);

  useEffect(() => {
    if (userData && userData.user) {
      const { checkInStreak, lastCheckInDate } = userData.user;
      
      setCurrentStreak(checkInStreak || 0);

      // Check if they can claim today
      if (!lastCheckInDate) {
        setCanClaimToday(true);
      } else {
        const lastCheckInObj = new Date(lastCheckInDate);
        lastCheckInObj.setUTCHours(0, 0, 0, 0);

        const today = new Date();
        today.setUTCHours(0, 0, 0, 0);

        const diffTime = Math.abs(today - lastCheckInObj);
        const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays === 0) {
          setCanClaimToday(false); // Already claimed
        } else if (diffDays === 1) {
          setCanClaimToday(true); // Ready for next day
        } else {
          // Missed a day
          setCanClaimToday(true);
          setCurrentStreak(0); // Will reset on server anyway
        }
      }
    }
  }, [userData]);

  const handleClaim = async () => {
    if (!canClaimToday || loading) return;
    
    haptic.tap();
    setLoading(true);

    try {
      const response = await axios.post(
        `${API_URL}/api/user/${userData.userId}/checkin`,
        { initData: WebApp.initData },
        { headers: { "x-api-key": APIKey } }
      );

      if (response.data.success) {
        haptic.success();
        confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
        
        const { reward, streak, user: updatedUserFields } = response.data;
        
        let msg = `Day ${streak} claimed: ${reward.gems} Gems!`;
        if (reward.tickets > 0) msg += ` + ${reward.tickets} Ticket!`;
        toast.success(msg, { position: "bottom-center", autoClose: 3000 });

        // Update context so UI reflects seamlessly
        setUserData(prev => ({
          ...prev,
          user: {
            ...prev.user,
            points: updatedUserFields.points,
            tickets: updatedUserFields.tickets,
            checkInStreak: updatedUserFields.checkInStreak,
            lastCheckInDate: updatedUserFields.lastCheckInDate
          }
        }));

        setCanClaimToday(false);
        setCurrentStreak(streak);
      }
    } catch (error) {
      console.error("Check-in error:", error);
      haptic.error();
      if (error.response?.data?.status === "claimed_today") {
        toast.info("You already claimed today's reward!", { position: "bottom-center" });
        setCanClaimToday(false);
      } else {
        toast.error("Failed to claim daily reward.", { position: "bottom-center" });
      }
    } finally {
      setLoading(false);
    }
  };

  if (!userData?.user) return null;

  // Calculate which day is active
  // If canClaimToday is true, active day is currentStreak + 1 (or 1 if no streak/missed)
  // If false, active day is currentStreak (because they just claimed it today)
  let pendingDayNumber = 1;
  if (canClaimToday) {
     pendingDayNumber = currentStreak >= 7 ? 1 : currentStreak + 1;
  } else {
     pendingDayNumber = currentStreak; // The one they already claimed today
  }

  return (
    <div className="bg-white dark:bg-slate-800/80 backdrop-blur-md rounded-3xl p-4 mb-6 shadow-sm border border-slate-200 dark:border-slate-700/50">
      <div className="flex justify-between items-end mb-3">
        <div>
          <h2 className="text-base font-extrabold text-slate-800 dark:text-white flex items-center gap-1.5">
            7-Day Streak
          </h2>
          <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">
            Come back daily for bigger rewards!
          </p>
        </div>
        <div className="text-right">
          <div className="text-xs font-bold text-[#9333EA] dark:text-purple-400">{currentStreak} <span className="opacity-70 text-[10px]">/ 7</span></div>
        </div>
      </div>

      {/* 2-Row Grid Area (4 items top, 3 items bottom) */}
      <div className="grid grid-cols-4 gap-2 flex-1">
        {STREAK_REWARDS.map((reward, idx) => {
          const isPast = reward.day <= currentStreak && (!canClaimToday || reward.day < pendingDayNumber);
          const isToday = reward.day === pendingDayNumber;
          const isFuture = reward.day > pendingDayNumber;

          // Day 7 special styling
          const isDay7 = reward.day === 7;

          return (
            <motion.div
              key={reward.day}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
              onClick={() => { if (isToday && canClaimToday) handleClaim(); }}
              className={`relative rounded-xl p-2 flex flex-col items-center justify-center transition-all min-h-[84px] ${
                isDay7 ? "col-span-2" : "col-span-1" // Make day 7 take up 2 columns to balance the bottom row
              } ${
                isPast
                  ? "bg-slate-100 dark:bg-slate-700/30 opacity-60 border border-transparent"
                  : isToday && canClaimToday
                  ? isDay7 
                    ? "bg-gradient-to-br from-amber-400 to-orange-500 shadow-[0_2px_15px_rgba(245,158,11,0.4)] border-amber-300 scale-105 cursor-pointer ring-2 ring-amber-400 z-10"
                    : "bg-gradient-to-br from-[#9333EA] to-purple-600 shadow-[0_2px_15px_rgba(147,51,234,0.3)] border-purple-500 scale-105 cursor-pointer ring-2 ring-purple-400 z-10"
                  : isToday && !canClaimToday
                  ? "bg-slate-100 dark:bg-slate-700/30 border border-transparent opacity-80" // Claimed today
                  : "bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700/50 opacity-90" // Future
              }`}
            >
              <div className={`text-[10px] uppercase font-bold tracking-tight mb-1 ${
                isPast || (isToday && !canClaimToday) ? "text-slate-400 dark:text-slate-500" 
                : isToday && canClaimToday ? "text-white"
                : "text-slate-400 dark:text-slate-500"
              }`}>
                Day {reward.day}
              </div>

              {/* Icon / Status */}
              <div className="h-6 flex flex-col items-center justify-center mb-1.5">
                {isPast || (isToday && !canClaimToday) ? (
                  <FaCheckCircle className="text-slate-400 dark:text-slate-500 text-sm" />
                ) : isToday && canClaimToday ? (
                  <motion.div
                    animate={{ y: [0, -3, 0] }}
                    transition={{ repeat: Infinity, duration: 1.5 }}
                  >
                    {isDay7 ? <FaTicketAlt className="text-white text-base drop-shadow-sm" /> : <FaGem className="text-white text-base drop-shadow-sm" />}
                  </motion.div>
                ) : (
                  <FaLock className="text-slate-300 dark:text-slate-600 text-sm" />
                )}
              </div>

              {/* Reward Value */}
              <div className={`text-xs font-bold text-center flex flex-col items-center leading-tight ${
                 isPast || (isToday && !canClaimToday) ? "text-slate-400 dark:text-slate-500" 
                 : isToday && canClaimToday ? "text-white"
                 : "text-[#9333EA] dark:text-purple-400"
              }`}>
                <span className="flex items-center gap-[2px]">{reward.gems} <FaGem className="text-[9px]" /></span>
                {reward.tickets > 0 && <span className="flex items-center gap-[2px] text-[10px] mt-0.5">{reward.tickets} <FaTicketAlt className="text-[8px]" /></span>}
              </div>

            </motion.div>
          );
        })}
      </div>

      {canClaimToday ? (
        <motion.button
          whileTap={{ scale: 0.96 }}
          onClick={handleClaim}
          disabled={loading}
          className="w-full py-3 mt-4 bg-gradient-to-r from-[#9333EA] to-purple-600 hover:from-[#7e22ce] hover:to-[#6b21a8] text-white font-bold rounded-2xl shadow-[0_4px_15px_rgba(147,51,234,0.25)] transition-all flex items-center justify-center gap-2 text-sm"
        >
          {loading ? "Claiming..." : `Claim Day ${pendingDayNumber} Reward!`}
        </motion.button>
      ) : (
        <div className="w-full py-3 mt-4 bg-slate-100 dark:bg-slate-700/40 text-slate-400 dark:text-slate-500 font-bold rounded-2xl text-center text-xs border border-slate-200 dark:border-slate-700/50">
          Come back tomorrow for Day {currentStreak >= 7 ? 1 : currentStreak + 1}
        </div>
      )}

    </div>
  );
};

export default DailyCheckIn;
